/**
 * PlatformAdapter Interface
 *
 * The contract that Layer 4 platform adapters implement.
 * Agent-side interception: governs what agents are allowed to do before they do it.
 *
 * Each adapter is 200-500 lines of platform-specific code.
 * All verification logic lives in the gateway — adapters just translate
 * between the platform's world and AstraSync's world.
 */

import type { AstraSyncGateway } from '../gateway/gateway';
import type { PDLSSContext, VerificationDecision, AgentAction, InterceptResult } from '../gateway/types';

export interface AdapterConfig {
  /** The AstraSyncGateway instance (handles mode routing) */
  gateway: AstraSyncGateway;
  /** Platform-specific configuration */
  adapterOptions: Record<string, unknown>;
}

export interface PlatformAdapter {
  /**
   * Interface version for compatibility checking.
   * Current version: 1.
   */
  readonly interfaceVersion: number;

  /**
   * Platform-specific initialization.
   * Load config, register hooks, establish connections.
   */
  initialize(config: AdapterConfig): Promise<void>;

  /**
   * Graceful shutdown.
   * Drain in-flight verifications, deregister hooks, close connections.
   */
  shutdown(): Promise<void>;

  /**
   * Intercept an agent action before execution.
   *
   * How this works depends on the platform:
   * - CLI adapter: proxy server captures outbound request
   * - Browser adapter: content script intercepts DOM interaction
   * - Express: middleware captures inbound request
   */
  interceptAction(action: AgentAction): Promise<InterceptResult>;

  /**
   * Extract PDLSS-compatible context from a platform-specific action.
   * Maps platform-native action format to the universal PDLSSContext.
   */
  extractContext(action: AgentAction): PDLSSContext;

  /**
   * Enforce the verification decision in a platform-specific way.
   *
   * How this works depends on the platform:
   * - CLI: block command / allow command / prompt for approval
   * - Browser: block navigation / show confirmation dialog
   * - Express: return 403 / pass through / inject headers
   */
  enforceDecision(decision: VerificationDecision): Promise<void>;
}

/**
 * Current adapter interface version.
 */
export const ADAPTER_INTERFACE_VERSION = 1;

/**
 * Check if an adapter is compatible with the current interface version.
 */
export function isCompatibleAdapter(adapter: PlatformAdapter): boolean {
  return adapter.interfaceVersion === ADAPTER_INTERFACE_VERSION;
}
